import React, { PureComponent, Children, isValidElement, cloneElement } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import throttle from '../util/throttle';
import ChevronLeft from '../icons/internals/ChevronLeft';
import ChevronRight from '../icons/internals/ChevronRight';
import IconButton from '../button/IconButton';

/**
 * Tabs
 * */
class Tabs extends PureComponent {
  static propTypes = {
    prefixCls: PropTypes.string,
    className: PropTypes.string,
    /** 是否显示墨水条 */
    showInkbar: PropTypes.bool,
    /** 是否占满全宽 */
    fullWidth: PropTypes.bool,
    /** 是否可以滚动 */
    scrollable: PropTypes.bool,
    /** 默认值 */
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    /** tab切换回调 function(e, value) */
    onChange: PropTypes.func,
    /** tab点击回调 function(e, value) */
    onSelect: PropTypes.func,
    /** 尺寸 */
    size: PropTypes.oneOf(['large', 'medium', 'small']),
  };

  static defaultProps = {
    defaultValue: 0,
    prefixCls: 'ne-tabs',
    showInkbar: true,
    fullWidth: false,
    scrollable: false,
    size: 'medium',
  };

  constructor(props, context) {
    super(props, context);
    this.isControlled = props.value != null;
    this.state = Object.assign(
      {
        inkbarStyle: {},
        disableLeftButton: false,
        disableRightButton: false,
      },
      this.isControlled ? {} : { selectedValue: props.defaultValue },
    );
    this.tabItems = {};
  }

  componentDidMount() {
    this.init();
    setTimeout(this.init, 300);
  }

  componentWillReceiveProps(newProps) {
    if (!this.isControlled && newProps.defaultValue !== this.props.defaultValue) {
      this.setState({
        selectedValue: newProps.defaultValue,
      });
    }
  }

  componentDidUpdate() {
    this.updateInkbar();
  }

  valueToIndex = {};

  scrollLeft = 0;

  init = () => {
    const { scrollable } = this.props;
    this.updateInkbar();
    scrollable && this.scrollToDefaultTab();
    scrollable && this.updateScrollButtonState();
    window.addEventListener(
      'resize',
      throttle(() => {
        this.updateInkbar();
        scrollable && this.updateScrollButtonState();
      }, 500),
    );
  };

  getTabs(props = this.props) {
    const tabs = [];
    Children.forEach(props.children, tab => {
      if (isValidElement(tab)) {
        tabs.push(tab);
      }
    });
    return tabs;
  }

  getDefaultTabPosition() {
    const { value } = this.props;
    const { selectedValue } = this.state;
    const targetIndex = this.valueToIndex[this.isControlled ? value : selectedValue];
    const newStyle = {};
    if (this.tabs && targetIndex != null) {
      const target = this.tabs.children[targetIndex];
      if (target) {
        newStyle.left =
          (target.getBoundingClientRect().left || 0) -
          (this.tabs.getBoundingClientRect().left || 0);
        newStyle.width = target.getBoundingClientRect().width;
      }
    }
    return newStyle;
  }

  scrollTo(distance) {
    if (this.tabs) {
      this.scrollLeft = distance;
      this.tabs.style.transform = `translateX(${-this.scrollLeft}px)`;
    }
  }

  scrollToDefaultTab = () => {
    const newStyle = this.getDefaultTabPosition();
    if (
      newStyle.left != null &&
      (newStyle.left - this.scrollLeft > this.scroller.clientWidth - newStyle.width + 10 ||
        newStyle.left - this.scrollLeft < -10) // 10表示隐藏宽度小于10时不滚动
    ) {
      this.scrollTo(newStyle.left);
      // this.forceUpdate();
    }
  };

  updateInkbar = () => {
    const { inkbarStyle } = this.state;
    const newStyle = this.getDefaultTabPosition();
    if (
      (newStyle.left !== inkbarStyle.left || newStyle.width !== inkbarStyle.width) &&
      !Number.isNaN(newStyle.left) &&
      !Number.isNaN(newStyle.width)
    ) {
      this.setState({ inkbarStyle: newStyle });
    }
  };

  getTabsEl = el => {
    this.tabs = el;
  };

  getScrollerEl = el => {
    this.scroller = el;
  };

  updateScrollButtonState = () => {
    this.setState({
      disableRightButton:
        !this.tabs ||
        !this.scroller ||
        this.scrollLeft + this.scroller.clientWidth >= this.tabs.clientWidth,
      disableLeftButton: !this.tabs || !this.scroller || this.scrollLeft <= 0,
    });
  };

  onLeftScrollClick = () => {
    if (this.tabs && this.scroller) {
      const newLeft =
        this.scrollLeft - this.scroller.clientWidth + this.tabs.children[0].clientWidth;
      this.scrollTo(newLeft <= 0 ? 0 : newLeft);
      this.updateScrollButtonState();
    }
  };

  onRightScrollClick = () => {
    if (this.tabs && this.scroller) {
      const newLeft =
        this.scrollLeft + this.scroller.clientWidth - this.tabs.children[0].clientWidth;
      this.scrollTo(newLeft >= this.tabs.clientWidth ? this.tabs.clientWidth : newLeft);
      this.updateScrollButtonState();
    }
  };

  onScroll = e => {
    // console.log(e.touches[0]);
  };

  onSelect = (e, tabValue) => {
    const { onChange, onSelect, scrollable, value } = this.props;
    const { selectedValue } = this.state;
    if (!this.isControlled) {
      this.setState({ selectedValue: tabValue }, () => {
        if (scrollable) {
          this.scrollToDefaultTab();
          this.updateScrollButtonState();
        }
      });
    }
    if (onChange && (this.isControlled ? value : selectedValue) !== tabValue) {
      onChange(e, tabValue);
    }

    if (onSelect) {
      onSelect(e, tabValue);
    }
  };

  render() {
    const {
      children,
      prefixCls,
      className,
      showInkbar,
      fullWidth,
      scrollable,
      defaultValue,
      size,
      value: propsValue,
      ...other
    } = this.props;
    const { selectedValue, disableLeftButton, disableRightButton, inkbarStyle } = this.state;
    const value = this.isControlled ? propsValue : selectedValue;
    const classes = classNames(prefixCls);
    const tabs = this.getTabs().map((tab, key) => {
      this.valueToIndex[tab.props.value != null ? tab.props.value : key] = key;
      return cloneElement(tab, {
        fullWidth,
        index: key,
        size,
        key: tab.props.value || key,
        selected: tab.props.value === value || (tab.props.value == null && key === value),
        onSelect: this.onSelect,
      });
    });
    return (
      <div
        {...other}
        className={classNames(`${prefixCls}-container`, className, `${prefixCls}-${size}`)}>
        {scrollable ? (
          <IconButton
            variant="text"
            className={`${prefixCls}-scroll-button`}
            onClick={this.onLeftScrollClick}
            disabled={disableLeftButton}>
            <ChevronLeft className={`${prefixCls}-scroll-button-icon`} />
          </IconButton>
        ) : null}
        <div className={`${prefixCls}-scroller`} ref={this.getScrollerEl}>
          <div
            className={classes}
            role="tablist"
            ref={this.getTabsEl}
            style={{
              transform: `translateX(${-this.scrollLeft}px)`,
            }}>
            {tabs}
            {showInkbar ? <div className={`${prefixCls}-inkbar`} style={inkbarStyle} /> : null}
          </div>
        </div>
        {scrollable ? (
          <IconButton
            variant="text"
            className={`${prefixCls}-scroll-button`}
            onClick={this.onRightScrollClick}
            disabled={disableRightButton}>
            <ChevronRight className={`${prefixCls}-scroll-button-icon`} />
          </IconButton>
        ) : null}
      </div>
    );
  }
}
export default Tabs;



// WEBPACK FOOTER //
// ./src/tabs/Tabs.jsx